import { Loader2 } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useListDocuments } from "~/api/default/default";
import { DoclistTreeItemSectionsItem, Page } from "~/api/model";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";

import { Button } from "~/components/ui/button";
import { useChatDocumentParams } from "~/helpers";

interface SectionWithSourceInfo {
  section: DoclistTreeItemSectionsItem;
  sourceFile: string;
  pageRange: string;
}

interface DeletePageDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCancel: () => void;
  onDelete: () => void;
  selectedCount?: number;
  type?: "page" | "section";
  name?: string;
  sectionNames?: string[];
  sectionsToDelete?: DoclistTreeItemSectionsItem[];
  sectionsToKeep?: DoclistTreeItemSectionsItem[];
  isDeleting?: boolean;
}

const DELAY_BEFORE_DELETE_ENABLED_MS = 2000;

export const DeletePageDialog: FC<DeletePageDialogProps> = ({
  open,
  setOpen,
  onCancel,
  onDelete,
  selectedCount = 1,
  type = "page",
  name,
  sectionNames = [],
  sectionsToDelete = [],
  sectionsToKeep = [],
  isDeleting = false,
}) => {
  const intl = useIntl();
  const { organizationId, caseId } = useChatDocumentParams();
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false);

  const { data: documentsData } = useListDocuments(
    organizationId || "",
    caseId || "",
    {
      query: {
        enabled:
          !!organizationId && !!caseId && open && sectionsToDelete.length > 0,
      },
    }
  );

  const documentMap = useMemo(() => {
    if (!documentsData?.documents) return new Map<string, string>();
    return new Map(documentsData.documents.map((d) => [d.id, d.filename]));
  }, [documentsData?.documents]);

  const getSourceInfo = (
    section: DoclistTreeItemSectionsItem
  ): SectionWithSourceInfo => {
    const pages = (section.pages || []) as Page[];
    const documentIds = [
      ...new Set(pages.map((p) => p.documentId).filter(Boolean)),
    ];
    const pageNums = pages
      .map((p) => p.originPageNum)
      .filter((n) => n !== undefined);

    const sourceFiles = documentIds
      .map((id) => documentMap.get(id))
      .filter(Boolean) as string[];
    const sourceFile =
      sourceFiles.length > 0
        ? sourceFiles[0]
        : intl.formatMessage({
            id: "docList.deleteSections.unknownSource",
            defaultMessage: "Unknown source",
          });

    let pageRange = "";
    if (pageNums.length > 0) {
      const minPage = Math.min(...pageNums);
      const maxPage = Math.max(...pageNums);
      pageRange = minPage === maxPage ? `${minPage}` : `${minPage}-${maxPage}`;
    }

    return { section, sourceFile, pageRange };
  };

  const sectionsWithSourceInfo = useMemo(() => {
    return sectionsToDelete.map(getSourceInfo);
  }, [sectionsToDelete, documentMap, intl]);

  const groupedBySource = useMemo(() => {
    const groups = new Map<string, SectionWithSourceInfo[]>();
    sectionsWithSourceInfo.forEach((info) => {
      const existing = groups.get(info.sourceFile) || [];
      existing.push(info);
      groups.set(info.sourceFile, existing);
    });
    return groups;
  }, [sectionsWithSourceInfo]);

  useEffect(() => {
    if (open && sectionsToDelete.length > 0) {
      setIsDeleteEnabled(false);
      const timer = setTimeout(() => {
        setIsDeleteEnabled(true);
      }, DELAY_BEFORE_DELETE_ENABLED_MS);
      return () => clearTimeout(timer);
    } else if (open) {
      setIsDeleteEnabled(true);
    }
  }, [open, sectionsToDelete.length]);

  useEffect(() => {
    if (!open) {
      setIsDeleteEnabled(false);
    }
  }, [open]);

  const isMultiplePages = selectedCount > 1;
  const isMultipleSections = type === "section" && selectedCount > 1;
  const hasEnhancedSectionData = sectionsToDelete.length > 0;
  const deleteButtonDisabled =
    isDeleting || (hasEnhancedSectionData && !isDeleteEnabled);

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {type === "page" ? (
              <FormattedMessage
                id={
                  isMultiplePages
                    ? "docList.deletePages.title"
                    : "docList.deletePage.title"
                }
                defaultMessage={
                  isMultiplePages ? "Delete Pages" : "Delete Page"
                }
              />
            ) : hasEnhancedSectionData ? (
              <FormattedMessage
                id="docList.deleteSections.areYouSureTitle"
                defaultMessage="Are you sure?"
              />
            ) : (
              <FormattedMessage
                id={
                  isMultipleSections
                    ? "docList.deleteSections.title"
                    : "docList.deleteSection.title"
                }
                defaultMessage={
                  isMultipleSections ? "Delete Sections" : "Delete Section"
                }
              />
            )}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription className="flex flex-col gap-[20px]">
          {type === "page" ? (
            <span>
              {isMultiplePages ? (
                <FormattedMessage
                  id="docList.deletePages.areYouSure"
                  values={{ count: selectedCount }}
                />
              ) : (
                <FormattedMessage
                  id="docList.deletePage.areYouSure"
                  defaultMessage="Are you sure you want to remove the page from this version?"
                />
              )}
            </span>
          ) : (
            <div className="flex flex-col gap-3">
              {hasEnhancedSectionData ? (
                <>
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-destructive">
                      <FormattedMessage
                        id="docList.deleteSections.deletingLabel"
                        defaultMessage="Deleting:"
                      />
                    </span>
                    <div className="max-h-[150px] overflow-y-auto border border-destructive/30 rounded-md p-2 bg-destructive/5">
                      {Array.from(groupedBySource.entries()).map(
                        ([sourceFile, sections]) => (
                          <div key={sourceFile} className="mb-2 last:mb-0">
                            <div
                              className="text-xs font-medium text-muted-foreground mb-1"
                              style={{ overflowWrap: "anywhere" }}
                            >
                              <FormattedMessage
                                id="docList.deleteSections.fromFile"
                                defaultMessage='From "{filename}":'
                                values={{ filename: sourceFile }}
                              />
                            </div>
                            <ul className="space-y-1">
                              {sections.map((info) => (
                                <li
                                  key={info.section.id}
                                  className="flex items-start gap-2 text-sm"
                                >
                                  <span className="text-destructive font-medium shrink-0">
                                    ×
                                  </span>
                                  <span className="flex-1 min-w-0">
                                    <span className="font-medium break-words">
                                      {info.section.name}
                                    </span>
                                    {info.pageRange && (
                                      <span className="text-muted-foreground ml-1">
                                        <FormattedMessage
                                          id="docList.deleteSections.pageRange"
                                          defaultMessage="(pages {range})"
                                          values={{ range: info.pageRange }}
                                        />
                                      </span>
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  {sectionsToKeep.length > 0 && (
                    <div className="space-y-2">
                      <span className="text-sm font-medium text-green-600">
                        <FormattedMessage
                          id="docList.deleteSections.keepingLabel"
                          defaultMessage="Keeping:"
                        />
                      </span>
                      <div className="max-h-[150px] overflow-y-auto border border-green-600/30 rounded-md p-2 bg-green-600/5">
                        <ul className="space-y-1">
                          {sectionsToKeep.map((section) => {
                            const info = getSourceInfo(section);
                            return (
                              <li
                                key={section.id}
                                className="flex items-start gap-2 text-sm"
                              >
                                <span className="text-green-600 font-medium shrink-0">
                                  ✓
                                </span>
                                <span className="flex-1 min-w-0">
                                  <span className="font-medium break-words">
                                    {section.name}
                                  </span>
                                  {info.pageRange && (
                                    <span className="text-muted-foreground ml-1">
                                      <FormattedMessage
                                        id="docList.deleteSections.pageRange"
                                        defaultMessage="(pages {range})"
                                        values={{ range: info.pageRange }}
                                      />
                                    </span>
                                  )}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              ) : isMultipleSections ? (
                <>
                  <span>
                    <FormattedMessage
                      id="docList.deleteSections.areYouSure"
                      defaultMessage="Are you sure you want to remove the following sections from this version?"
                    />
                  </span>
                  <ul className="list-disc list-inside max-h-[200px] overflow-y-auto prose prose-sm dark:prose-invert">
                    {sectionNames.map((sectionName, index) => (
                      <li key={index} className="text-sm">
                        <strong>{sectionName}</strong>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <span>
                  <FormattedMessage
                    id="docList.deleteSection.areYouSure"
                    defaultMessage="Are you sure you want to remove the section {name} from this version?"
                    values={{ name: <strong>{name}</strong> }}
                  />
                </span>
              )}
            </div>
          )}
          <span>
            <FormattedMessage
              id="docList.deletePage.aboutChanges"
              defaultMessage="The changes made will not affect the original files."
            />
          </span>
        </AlertDialogDescription>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isDeleting}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={handleDelete}
            variant="destructive"
            disabled={deleteButtonDisabled}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <FormattedMessage id="deleting" defaultMessage="Deleting..." />
              </>
            ) : hasEnhancedSectionData ? (
              <FormattedMessage
                id="docList.deleteSections.deleteButtonDuplicates"
                defaultMessage="Delete {count} {count, plural, one {Duplicate} other {Duplicates}}"
                values={{ count: sectionsToDelete.length }}
              />
            ) : (
              <FormattedMessage id="delete" defaultMessage="Delete" />
            )}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
